import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";
import { Mention, MentionsInput } from "react-mentions";

import { defaultMentionStyles, defaultStyles } from "../../../../../../config";
import AttachmentBar from "../../../../../AttachmentBar";
import { Select } from "../../../../../common";
import AttachmentViewer from "../../../../../common/AttachmentViewer";
import TranslateButton from "../../../../../common/TranslateButton";
import { SendMsgIcon } from "../../../../../icons/svg";

interface MessageBodyProps {
  messageBody: string;
  setMessageBody: (value: string) => void;
  attachments: any[];
  setAttachments: any;
  device: string;
  setDevice: (value: string) => void;
  fetchUsers: any; // Define the type based on your fetch function
  charCount: number;
  perMessageLimit: number;
  isLimitReached: boolean;
  isGuestMode: boolean;
  clickedRow: any; // Define the type based on clickedRow structure
  lineDevices: any[]; // Define the type of lineDevices
  shouldBtnDisabled: () => boolean;
  sendMessage: () => void;
  setMentions: any;
}

const MessageBody: React.FC<MessageBodyProps> = ({
  messageBody,
  setMessageBody,
  attachments,
  setAttachments,
  device,
  setDevice,
  fetchUsers,
  charCount,
  perMessageLimit,
  isLimitReached,
  isGuestMode,
  clickedRow,
  lineDevices,
  shouldBtnDisabled,
  sendMessage,
  setMentions,
}) => {
  const maxCharLimit = 1600; // Set your max character limit

  return (
    <VStack
      w="100%"
      alignItems="flex-start"
      gap="0"
      background="white"
      position="absolute"
      borderBottomRadius="0.5rem"
      bottom="0"
      left="0"
    >
      <AttachmentBar
        type="sms"
        setBody={(text, concatText, type = "text") => {
          if (type === "text") {
            const messagesText = `${messageBody} ${
              concatText ? `${concatText}: ` : ""
            }${text}`;
            setMessageBody(messagesText);
          } else {
            setAttachments((prev: any) => [...prev, text]);
          }
        }}
        handleTemplate={(value: string, body, attachmentUrls) => {
          setMessageBody(value);
          setAttachments(attachmentUrls);
        }}
        contactId={clickedRow?.uuid}
        isGuestMode={isGuestMode}
        setAttachments={setAttachments}
      />

      <AttachmentViewer
        attachments={attachments}
        setAttachments={setAttachments}
      />
      {!isGuestMode && (
        <HStack w="100%" padding="0">
          <Select
            onChange={(value: any) => setDevice(value.value)}
            placeholder="Select Line"
            options={
              lineDevices?.map((lineDevice: any) => ({
                value: lineDevice.phoneNumber,
                label: lineDevice.phoneNumber,
              })) || []
            }
            variant="default"
            w="100%"
            value={device}
          />
        </HStack>
      )}

      {/* Character count progress bar */}
      <Box mt="0.5rem" width="100%" marginLeft="1rem">
        <Text
          fontSize="0.75rem"
          color={isLimitReached ? "var(--red-600)" : "var(--grey-500)"}
        >
          <strong>Message#{Math.ceil(charCount / perMessageLimit)}</strong>:{" "}
          {charCount % perMessageLimit}/{perMessageLimit}
        </Text>
        {isLimitReached && (
          <Text fontSize="0.75rem" color="var(--red-600)">
            The message body has reached the allowed message limit.
          </Text>
        )}
      </Box>

      <HStack
        w="100%"
        padding="0"
        alignItems="flex-start"
        justifyContent="space-between"
        style={{
          pointerEvents:
            isGuestMode || clickedRow?.phone_number ? "auto" : "none",
          opacity: isGuestMode || clickedRow?.phone_number ? 1 : 0.5,
          cursor:
            isGuestMode || clickedRow?.phone_number ? "text" : "not-allowed",
        }}
      >
        <Box w="88%">
          <MentionsInput
            value={messageBody}
            onChange={(_: any, newValue: string) =>
              newValue.length > maxCharLimit || setMessageBody(newValue)
            }
            style={{ ...defaultStyles, width: "100%" }}
          >
            <Mention
              displayTransform={(id: string, display: string) => `@${display}`}
              trigger="@"
              data={fetchUsers}
              style={defaultMentionStyles}
              appendSpaceOnAdd
              onAdd={(id: any) => setMentions((prev: any) => [...prev, id])}
            />
          </MentionsInput>
        </Box>

        <VStack alignItems="flex-end" mr="1.5rem">
          <Box w="34px">
            {!isGuestMode && (
              <TranslateButton
                value={messageBody}
                onChange={(translatedContent: any) =>
                  setMessageBody(translatedContent)
                }
              />
            )}
          </Box>
          <Button
            variant="none"
            padding="0.5rem"
            borderRadius="0.5rem"
            mb="6px"
            background="var(--primary-600)"
            isDisabled={shouldBtnDisabled()}
            onClick={sendMessage}
          >
            <SendMsgIcon width={20} height={20} />
          </Button>
        </VStack>
      </HStack>
    </VStack>
  );
};

export default MessageBody;
